/**
 * biometricLocal.js
 * 
 * Frontend utility for communicating directly with the Mantra RD Service 
 * on the local machine (127.0.0.1). This bypasses the need for a backend 
 * proxy when the backend is on a remote server.
 */

const RD_PORTS = [8030, ...Array.from({ length: 21 }, (_, i) => 11100 + i)];
const MORFIN_PORT = 8030;

/**
 * Extracts raw base64 template from an XML response.
 */
export const extractTemplateFromXML = (xmlOrRaw) => {
  if (!xmlOrRaw) return null;
  if (typeof xmlOrRaw === 'string' && xmlOrRaw.trim().startsWith('<')) {
    return (xmlOrRaw.match(/<Data[^>]*>([^<]+)<\/Data>/) || [])[1]?.trim() || xmlOrRaw;
  }
  return xmlOrRaw;
};

/**
 * Performs local fingerprint matching using MorFinAuth's /verify endpoint.
 * Requires the client to be running MorFinAuth (port 8030).
 */
export const verifyFingerprintLocal = async (connection, probTemplate, galleryTemplate) => {
  if (!connection || connection.type !== 'MorFinAuth') {
    return false;
  }
  try {
    const res = await fetch(`${connection.protocol}://127.0.0.1:${connection.port}/morfinauth/verify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ProbTemplate: probTemplate,
        GalleryTemplate: galleryTemplate,
        TmpFormat: 0
      })
    });
    const data = await res.json();
    return data.ErrorCode === '0' && data.Status === true;
  } catch (err) {
    return false;
  }
};

/**
 * Discovers the active RD Service port by probing each port.
 * @returns {Promise<{port: number, protocol: string, type: 'MorFinAuth'|'StandardRD'}|null>}
 */
export const discoverRDPort = async (onProgress) => {
  // Prioritize 8030 (Client Service / MorFinAuth) as it's most reliable for web browsers
  const PORTS = [8030, 11100, 11101, 11102, 11103, 11104, 11105];
  let resolved = false;

  console.log('🔍 Searching for local biometric scanner...');

  return new Promise((resolve) => {
    const resolveFound = (port, protocol, type, detail) => {
      if (resolved) return;
      resolved = true;
      console.log(`✅ Biometric Service Found! Protocol: ${protocol}, Port: ${port}, Type: ${type}, Detail: ${detail}`);
      if (onProgress) onProgress({ port, protocol, status: 'found', type, detail });
      resolve({ port, protocol, type });
    };

    const tryPort = async (port) => {
      // We try both localhost and 127.0.0.1 as browsers sometimes treat them differently
      const hosts = ['127.0.0.1', 'localhost'];
      const protocols = ['http', 'https']; // Check both protocols for mixed-content support
      
      for (const host of hosts) {
        for (const protocol of protocols) {
          if (resolved) return;
          
          try {
            // Use fetch with 'no-cors'. This allows the request to go through 
            // even if the RD service doesn't have CORS headers.
            // We won't be able to read the body, but a successful connection 
            // (even if it's an opaque response) means the service is ALIVE.
            await fetch(`${protocol}://${host}:${port}/`, { mode: 'no-cors', cache: 'no-cache' });
            
            if (!resolved) {
              resolveFound(port, protocol, port === 8030 ? 'MorFinAuth' : 'StandardRD', `Detected via ${protocol}://${host}`);
              return;
            }
          } catch (e) {
            // If it throws, the port is likely closed, or mixed-content/cert issue blocked it
          }
        }
      }
    };

    PORTS.forEach(tryPort);

    // Final fallback timeout
    setTimeout(() => {
      if (!resolved) {
        console.warn('❌ No biometric service detected after 4 seconds.');
        resolve(null);
      }
    }, 4000);
  });
};

/**
 * Captures a fingerprint from the local RD Service.
 * @param {Object} connection - {port, protocol, type} from discoverRDPort
 * @param {string} pidOptions - XML PidOptions
 * @returns {Promise<string>} - PidData XML or Error XML
 */
export const rdCaptureLocal = async (connection, pidOptions) => {
  const { port, protocol, type } = connection;

  if (type === 'MorFinAuth') {
    return captureMorFin(port, protocol);
  } else {
    return captureStandard(port, protocol, pidOptions);
  }
};

/**
 * Mantra MorFinAuth (JSON API) Capture
 */
const captureMorFin = async (port, protocol) => {
  try {
    const baseUrl = `${protocol}://localhost:${port}/morfinauth`;
    
    // 1. Capture
    const captureRes = await fetch(`${baseUrl}/capture`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ Quality: 60, TimeOut: 10 })
    }).then(r => r.json());

    if (captureRes.ErrorCode !== "0") {
      return `<?xml version="1.0"?><PidData><Resp errCode="${captureRes.ErrorCode}" errInfo="${captureRes.ErrorDescription}" /></PidData>`;
    }

    // 2. Get Template
    const templateRes = await fetch(`${baseUrl}/gettemplate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ TmpFormat: 0 })
    }).then(r => r.json());

    if (templateRes.ErrorCode !== "0") {
      return `<?xml version="1.0"?><PidData><Resp errCode="${templateRes.ErrorCode}" errInfo="${templateRes.ErrorDescription}" /></PidData>`;
    }

    // 3. Transform to PidData XML
    return `<?xml version="1.0"?>
<PidData>
  <Resp errCode="0" errInfo="Success" nmPoints="0" qScore="60" />
  <DeviceInfo dpId="" rdsId="" rdsVer="" mi="" mc="">
    <additional_info>
      <Param name="srno" value="${templateRes.SerialNo || 'MFS500'}" />
    </additional_info>
  </DeviceInfo>
  <Data type="X">${templateRes.ImgData || templateRes.Template}</Data>
</PidData>`;

  } catch (err) {
    return `<?xml version="1.0"?><PidData><Resp errCode="-999" errInfo="Local Capture Error: ${err.message}" /></PidData>`;
  }
};

/**
 * Standard RD Service (XML API) Capture
 * Uses the non-standard CAPTURE method.
 */
const captureStandard = (port, protocol, pidOptions) => {
  return new Promise((resolve) => {
    const url = `${protocol}://localhost:${port}/rd/capture`;
    const xhr = new XMLHttpRequest();
    
    // Most browsers block the custom "CAPTURE" verb. 
    // We try "POST" as many RD services allow it as a fallback for browsers.
    xhr.open('POST', url, true);
    xhr.setRequestHeader('Content-Type', 'text/xml');
    xhr.timeout = 20000;

    xhr.onload = () => {
      if (xhr.status === 200) {
        resolve(xhr.responseText);
      } else {
        // If POST is blocked/fails, try one last time with CAPTURE (though likely to fail in Chrome)
        const xhrRetry = new XMLHttpRequest();
        try {
          xhrRetry.open('CAPTURE', url, true);
          xhrRetry.setRequestHeader('Content-Type', 'text/xml');
          xhrRetry.timeout = 20000;
          xhrRetry.onload = () => resolve(xhrRetry.responseText);
          xhrRetry.onerror = () => resolve(`<?xml version="1.0"?><PidData><Resp errCode="-1" errInfo="Browser blocked scanner command (CAPTURE). Please install Mantra MorFinAuth service for browser support." /></PidData>`);
          xhrRetry.send(pidOptions);
        } catch (e) {
          resolve(`<?xml version="1.0"?><PidData><Resp errCode="-1" errInfo="Browser blocked scanner command. Use MorFinAuth." /></PidData>`);
        }
      }
    };

    xhr.onerror = () => {
      resolve(`<?xml version="1.0"?><PidData><Resp errCode="-1" errInfo="Could not connect to scanner. Check if Mantra RD Service is running." /></PidData>`);
    };

    xhr.ontimeout = () => {
      resolve(`<?xml version="1.0"?><PidData><Resp errCode="-1" errInfo="Hardware Timeout" /></PidData>`);
    };

    xhr.send(pidOptions);
  });
};
